import { inject, Injectable } from '@angular/core';
import { type Observable, of } from 'rxjs';

import { type ReadCsvDataResponse } from '@blaze-shared/api-contracts-domain';
import { DatasetsApiService } from '@blaze-shared/datasets-data-access';
import { type RuntimeContext } from '@blaze-shared/elements-domain';
import { IntelliSenseService } from '@blaze-shared/intellisense-feature';
import { SchemaService } from '@blaze-shared/schema-data-access';
import { type ReadCsvDataWorkflowNode } from '@blaze-shared/workflow-domain';

/**
 * Reads a CSV file into rows.
 *
 * The node's URL may be an expression — that is what the `$$`/`@@` prefixes mark — in which case it
 * is evaluated against the node's input first.
 */
@Injectable({ providedIn: 'root' })
export class ReadCsvActionService {
  private readonly datasetsApiService = inject(DatasetsApiService);
  private readonly iss = inject(IntelliSenseService);
  private readonly schemaService = inject(SchemaService);

  readCsvData(
    node: ReadCsvDataWorkflowNode,
    nodeInput: any,
    ctx: RuntimeContext,
  ): Observable<ReadCsvDataResponse> {
    if (!nodeInput) {
      return of({ columns: [], data: [] });
    }
    const context = this.iss.updateRuntimeContext(ctx, nodeInput);
    const nodeCsvFileUrl = this.schemaService.replaceTagIdsWithValuesWhenString(node.csvFileUrl);
    const url =
      nodeCsvFileUrl?.startsWith('$$') || nodeCsvFileUrl?.startsWith('@@')
        ? this.iss.eval(nodeCsvFileUrl, context)
        : nodeCsvFileUrl;
    return this.datasetsApiService.postReadCsv(url);
  }
}
